import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Mail, MessageCircle, Smartphone } from 'lucide-react';
import { Drawer, Field } from '@/components/live';
import { OTP_FAILURES, acknowledgeConsent, otpChannels, sendOtp, verifyOtp, type IssuedOtp } from '@/api/otp';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';
import { consentTypeLabel } from '@/lib/consentTypes';
import type { ConsentRow } from '@/types/records';

const CHANNEL_ICONS = { whatsapp: MessageCircle, sms: Smartphone, email: Mail } as const;
const CHANNEL_NAME = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email' } as const;
type Channel = keyof typeof CHANNEL_NAME;

/** Where one code went, e.g. "WhatsApp ********4321, SMS ********4321". */
export interface OtpSentVia { channel: string; destination: string; channels: Array<{ channel: Channel; destination: string }> }

/**
 * Digital consent (owner, 2026-09-21): tick any of WhatsApp, SMS and email - the SAME code goes
 * to each, always to the party's own recorded contacts - read it back from the farmer, and the
 * consent is signed through OTP.
 */
export function ConsentCapture({
  consent,
  onClose,
  onDone,
  onSent,
}: {
  consent: ConsentRow;
  onClose: () => void;
  /** Called once the code is verified and the consent acknowledged, with where it was sent. */
  onDone: (via: OtpSentVia & { verifiedAt: string; otpEventId: string }) => void;
  /** Called once the code has gone out - used to generate the draft consent document at that moment. */
  onSent?: (via: OtpSentVia) => void;
}) {
  // A withdrawal is confirmed, not signed (found in the 2026-09-28 UI pass).
  const withdrawal = consent.consent_type === 'withdrawal';
  const [selected, setSelected] = useState<Channel[]>([]);
  const [sentVia, setSentVia] = useState<OtpSentVia | null>(null);
  const [issued, setIssued] = useState<IssuedOtp | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const channels = useQuery({
    queryKey: ['otp-channels', consent.party_id],
    queryFn: () => otpChannels(consent.party_id),
    enabled: isSupabaseConfigured,
  });

  // Start with every channel the party can actually be reached on ticked.
  useEffect(() => {
    setSelected((channels.data ?? []).filter((entry) => entry.available).map((entry) => entry.channel));
  }, [channels.data]);

  function toggle(channel: Channel) {
    setSelected((now) => (now.includes(channel) ? now.filter((entry) => entry !== channel) : [...now, channel]));
  }

  async function send() {
    setError(undefined);
    // The first ticked channel, in a fixed order, carries the code; the rest get the same code.
    const ordered = (['whatsapp', 'sms', 'email'] as Channel[]).filter((entry) => selected.includes(entry));
    const primary = ordered[0];
    if (!primary) { setError('Tick at least one way to send the code.'); return; }
    setBusy(true);
    try {
      const result = await sendOtp({
        purpose: 'consent',
        channel: primary,
        alsoChannels: ordered.slice(1),
        partyId: consent.party_id,
        linkedTable: 'party.party_consents',
        linkedId: consent.id,
      });
      setIssued(result);
      setCode('');
      const went = [
        { channel: primary, destination: result.destination_masked },
        ...(result.also ?? []).filter((entry) => entry.delivered).map((entry) => ({ channel: entry.channel, destination: entry.destination_masked })),
      ];
      const failed = (result.also ?? []).filter((entry) => !entry.delivered);
      const via: OtpSentVia = {
        channel: went.map((entry) => CHANNEL_NAME[entry.channel]).join(', '),
        destination: went.map((entry) => entry.destination).join(', '),
        channels: went,
      };
      setSentVia(via);
      toast.success(`The same code was sent by ${went.map((entry) => `${CHANNEL_NAME[entry.channel]} (${entry.destination})`).join(', ')}.`);
      if (failed.length > 0) toast.error(`Not sent by ${failed.map((entry) => CHANNEL_NAME[entry.channel]).join(', ')}: ${failed[0]?.error ?? 'provider error'}.`);
      onSent?.(via);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The code could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!issued) return;
    setError(undefined);
    setBusy(true);
    try {
      const result = await verifyOtp(issued.otp_event_id, code);
      if (!result.verified) {
        setError(OTP_FAILURES[result.failure_reason ?? ''] ?? 'That code could not be verified.');
        return;
      }
      await acknowledgeConsent(consent.id, issued.otp_event_id);
      toast.success(withdrawal ? 'Withdrawal confirmed through OTP.' : 'Consent signed through OTP.');
      onDone({ ...(sentVia ?? { channel: issued.channel, destination: issued.destination_masked, channels: [] }),
               verifiedAt: new Date().toISOString(), otpEventId: issued.otp_event_id });
    } catch (caught) {
      // A KYC refusal arrives here as its own sentence, which is the thing to show.
      setError(caught instanceof Error ? caught.message : 'The consent could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  const available = channels.data ?? [];

  return (
    <Drawer
      footer={
        <>
          <button className="cancel" onClick={onClose} type="button">Cancel</button>
          {issued
            ? <button className="primary" disabled={busy || code.length !== 6} onClick={() => void confirm()} type="button">
                {busy ? 'Checking...' : withdrawal ? 'Confirm the withdrawal' : 'Confirm and sign'}
              </button>
            : <button className="primary" disabled={busy} onClick={() => void send()} type="button">
                {busy ? 'Sending...' : 'Send code'}
              </button>}
        </>
      }
      onClose={onClose}
      open
      subtitle={consentTypeLabel(consent.consent_type)}
      title={withdrawal ? 'Withdrawal - confirm through OTP' : 'Digital consent - sign through OTP'}
    >
      <p className="muted" style={{ margin: 0 }}>
        One code goes to every way you tick, always to the party's own registered contacts. Read it
        back from them and enter it here - {withdrawal
          ? 'that confirms this withdrawal, and only this one. The code names what is withdrawn.'
          : 'that signs this consent, and only this one.'}
      </p>

      <Field label="Send the same code by (tick one or more)">
        <div className="login-channels multi">
          {available.map((entry) => {
            const Icon = CHANNEL_ICONS[entry.channel];
            const on = selected.includes(entry.channel);
            return (
              <button
                aria-pressed={on}
                className={on ? 'active' : ''}
                disabled={!entry.available || Boolean(issued)}
                key={entry.channel}
                onClick={() => toggle(entry.channel)}
                title={entry.available ? entry.destination_masked ?? undefined : 'Not recorded for this party'}
                type="button"
              >
                {on ? <Check size={16} /> : <Icon size={16} />} {CHANNEL_NAME[entry.channel]}
              </button>
            );
          })}
        </div>
      </Field>

      {available.length > 0 && (
        <ul className="otp-destinations">
          {available.map((entry) => (
            <li className={selected.includes(entry.channel) ? '' : 'muted'} key={entry.channel}>
              {CHANNEL_NAME[entry.channel]}: {entry.available ? entry.destination_masked : 'not recorded - add it on the farmer record'}
            </li>
          ))}
        </ul>
      )}

      {issued && (
        <Field label="Code from the farmer" required>
          <input
            autoFocus
            className="login-code"
            inputMode="numeric"
            maxLength={6}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            value={code}
          />
        </Field>
      )}

      {error && <p className="login-error" role="alert">{error}</p>}

      {issued && (
        <button disabled={busy} onClick={() => void send()} style={{ justifySelf: 'start' }} type="button">
          Send a new code
        </button>
      )}
    </Drawer>
  );
}
